import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { warn as devWarn } from "@/lib/devlog";
import {
  ChevronLeft, ChevronRight, ClipboardCheck, Save, AlertCircle,
  Clock, CalendarRange, Repeat, User as UserIcon, CalendarPlus, Trash2, Plus,
  Wallet,
} from "lucide-react";

const STATUS = [
  { id: "present", label: "P", title: "Present", cls: "bg-white text-brand-inkMuted border-brand-purple/15 hover:bg-[#F1F5FF]", active: "bg-[#22C55E] text-white border-[#22C55E]" },
  { id: "absent",  label: "A", title: "Absent",  cls: "bg-white text-brand-inkMuted border-brand-purple/15 hover:bg-[#F1F5FF]", active: "bg-[#EF4444] text-white border-[#EF4444]" },
  { id: "excuse",  label: "E", title: "Excuse",  cls: "bg-white text-brand-inkMuted border-brand-purple/15 hover:bg-[#F1F5FF]", active: "bg-[#F59E0B] text-white border-[#F59E0B]" },
];

/* ----------------- Local-time date helpers (avoid UTC drift) -------------- */
function parseISO(iso) {
  if (iso instanceof Date) return new Date(iso.getFullYear(), iso.getMonth(), iso.getDate());
  const [y, m, d] = String(iso).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}
function getMonday(d) {
  const dt = d instanceof Date ? new Date(d.getFullYear(), d.getMonth(), d.getDate()) : parseISO(d);
  const day = dt.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  dt.setDate(dt.getDate() + diff);
  return dt;
}
function fmtISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function shortDate(iso) {
  return parseISO(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
function fullDate(iso) {
  return parseISO(iso).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
}

export default function IndividualAttendancePage() {
  const { selectedSchool, selectedBranch, branchesForSelectedSchool, setSelectedBranchId } = useBranch();
  const { user } = useAuth();
  const canDeductRetention = (user?.role === "school_owner" || user?.role === "sub_owner") || !!(user?.permissions || {}).retention_fee_deduct;

  const [indClasses, setIndClasses] = useState([]);
  const [classId, setClassId] = useState(""); // optional filter
  const [sessionBalances, setSessionBalances] = useState({});

  // Day-centric view: pick a single calendar date, see every student who has
  // a session that day. Replaces the previous per-student weekly grid.
  const [selectedDate, setSelectedDate] = useState(fmtISO(new Date()));
  const [dayData, setDayData] = useState(null); // { date, sessions, marks }
  const [drafts, setDrafts] = useState({}); // { sessionKey: status }
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(""); // session key currently saving
  // Retention-fee deduction prompt — shown after marking a student ABSENT.
  const [retentionPrompt, setRetentionPrompt] = useState(null); // { student_id, student_name, date }
  const [makeups, setMakeups] = useState([]); // make-up sessions for the visible day
  // Per-row make-up dialog: which student do we want to log a make-up for?
  const [makeUpFor, setMakeUpFor] = useState(null);

  const branchId = selectedBranch?.id;

  // Load individual classes for the branch (for the optional filter dropdown).
  useEffect(() => {
    if (!branchId) { setIndClasses([]); return; }
    (async () => {
      try {
        const { data: c } = await api.get("/classes", { params: { branch_id: branchId } });
        setIndClasses((c || []).filter((cl) => cl.class_type === "individual"));
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [branchId]);

  // Load session balances for ALL students in the branch — so we can render
  // the per-student "🔵 paid / 🔴 unpaid" mini-chip on each row.
  const refreshBalances = useCallback(async () => {
    if (!branchId) return;
    try {
      const { data } = await api.get("/individual-attendance/session-balances", { params: { branch_id: branchId } });
      setSessionBalances(data?.balances || {});
    } catch { /* silent — chips are informational */ }
  }, [branchId]);

  useEffect(() => { refreshBalances(); }, [refreshBalances]);

  // Each session row is uniquely keyed by (student, start time) within a day.
  const sessionKey = (s) => `${s.student_id}|${s.time_start || ""}`;

  const loadDay = useCallback(async () => {
    if (!branchId || !selectedDate) { setDayData(null); setDrafts({}); setMakeups([]); return; }
    setLoading(true);
    try {
      const [{ data: d }, { data: mu }] = await Promise.all([
        api.get("/individual-attendance/by-day", {
          params: {
            branch_id: branchId,
            date: selectedDate,
            ...(classId ? { individual_class_id: classId } : {}),
          },
        }),
        api.get("/individual-attendance/make-up", {
          params: { branch_id: branchId, date_from: selectedDate, date_to: selectedDate },
        }).catch((e) => { devWarn("IA: make-up fetch failed", e?.message); return { data: [] }; }),
      ]);
      setDayData(d);
      setMakeups(mu || []);
      // Pre-fill drafts from saved marks so toggles reflect current state.
      const next = {};
      for (const s of (d.sessions || [])) {
        const k = sessionKey(s);
        const saved = d.marks?.[k];
        if (saved?.status) next[k] = saved.status;
      }
      setDrafts(next);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [branchId, selectedDate, classId]);

  useEffect(() => { loadDay(); }, [loadDay]);

  const shiftDay = (days) => {
    const d = parseISO(selectedDate);
    d.setDate(d.getDate() + days);
    setSelectedDate(fmtISO(d));
  };

  const toggleMark = (sess, status) => {
    const k = sessionKey(sess);
    setDrafts((prev) => ({ ...prev, [k]: prev[k] === status ? "" : status }));
  };

  const saveOne = async (sess) => {
    const k = sessionKey(sess);
    setSavingKey(k);
    try {
      const { data: r } = await api.put(`/individual-attendance?branch_id=${branchId}`, {
        student_id: sess.student_id,
        date: sess.date,
        time_start: sess.time_start || "",
        time_end: sess.time_end || "",
        teacher_employee_id: sess.teacher_employee_id || "",
        status: drafts[k] || "",
      });
      // Auto-consume + auto-create were both disabled on 2026-06-08 — the
      // backend now never touches Student Accounts when you mark attendance.
      // We just confirm the save with the student name + time.
      toast.success(`Saved ${sess.student_full_name}${sess.time_start ? " · " + sess.time_start : ""}`);
      // If this save marked the student ABSENT, prompt to deduct from the
      // Retention Fee balance (owner-like OR retention_fee_deduct perm).
      if (drafts[k] === "absent" && canDeductRetention) {
        setRetentionPrompt({
          student_id: sess.student_id,
          student_name: sess.student_full_name || "Student",
          date: sess.date,
        });
      }
      loadDay();
      refreshBalances();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingKey(""); }
  };

  const saveAll = async () => {
    if (!dayData?.sessions?.length) return;
    setSavingKey("__all");
    try {
      for (const s of dayData.sessions) {
        const k = sessionKey(s);
        await api.put(`/individual-attendance?branch_id=${branchId}`, {
          student_id: s.student_id,
          date: s.date,
          time_start: s.time_start || "",
          time_end: s.time_end || "",
          teacher_employee_id: s.teacher_employee_id || "",
          status: drafts[k] || "",
        });
      }
      toast.success("Day saved");
      loadDay();
      refreshBalances();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingKey(""); }
  };

  const stats = useMemo(() => {
    let p = 0, a = 0, e = 0, n = 0;
    const total = dayData?.sessions?.length || 0;
    for (const s of (dayData?.sessions || [])) {
      const v = drafts[sessionKey(s)];
      if (v === "present") p++;
      else if (v === "absent") a++;
      else if (v === "excuse") e++;
      else n++;
    }
    return { total, p, a, e, n };
  }, [dayData, drafts]);

  const deleteMakeUp = async (id) => {
    if (!window.confirm("Delete this make-up record?")) return;
    try {
      await api.delete(`/individual-attendance/make-up/${id}`);
      toast.success("Make-up deleted");
      loadDay();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const dayLabel = fullDate(selectedDate);

  return (
    <>
      <div className="mb-5">
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight">Individual Class Attendance</h1>
        <p className="text-brand-inkMuted mt-0.5 text-sm">Pick a date — see every student with an individual session that day. Mark each row Present / Absent / Excuse, then Save.</p>
      </div>

      {/* Filter bar */}
      <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-3 sm:p-4 mb-4" data-testid="ia-filters">
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <Field label="SCHOOL">
            <ReadInput value={selectedSchool?.name || "—"} />
          </Field>
          <Field label="BRANCH">
            <select value={branchId || ""} onChange={(e) => setSelectedBranchId(e.target.value)} className="ia-select" data-testid="ia-branch">
              {branchesForSelectedSchool.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
          <Field label="INDIVIDUAL CLASS (OPTIONAL)">
            <select value={classId} onChange={(e) => setClassId(e.target.value)} className="ia-select" data-testid="ia-class">
              <option value="">All individual classes</option>
              {indClasses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="DATE">
            <div className="inline-flex items-stretch rounded-2xl border border-brand-purple/20 bg-white overflow-hidden h-11" data-testid="ia-day">
              <button type="button" onClick={() => shiftDay(-1)} className="px-3 hover:bg-brand-purpleLight" data-testid="ia-day-prev"><ChevronLeft className="w-4 h-4" /></button>
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="border-0 bg-[#F6F8FE] rounded-none h-11 font-heading text-foreground min-w-[180px] text-center"
                data-testid="ia-day-input"
              />
              <button type="button" onClick={() => shiftDay(1)} className="px-3 hover:bg-brand-purpleLight" data-testid="ia-day-next"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </Field>
        </div>
        <p className="mt-3 text-xs text-brand-inkMuted font-heading" data-testid="ia-day-label">
          Viewing: <span className="text-foreground font-semibold">{dayLabel}</span>
        </p>
      </section>

      {/* Stats */}
      <section className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4" data-testid="ia-stats">
        <Stat label="SESSIONS" value={stats.total} tone="default" />
        <Stat label="PRESENT" value={stats.p} tone="green" />
        <Stat label="ABSENT" value={stats.a} tone="red" />
        <Stat label="EXCUSE" value={stats.e} tone="orange" />
        <Stat label="NOT MARKED" value={stats.n} tone="default" />
      </section>

      {/* Sessions list */}
      <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm overflow-hidden" data-testid="ia-sessions-wrap">
        {!selectedBranch ? (
          <div className="p-10 text-center" data-testid="ia-no-branch">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FFF3E0] flex items-center justify-center mb-3">
              <AlertCircle className="w-7 h-7 text-[#E65100]" />
            </div>
            <p className="font-heading text-lg font-semibold text-foreground">Pick a branch first</p>
          </div>
        ) : loading ? (
          <p className="p-6 text-brand-inkMuted">Loading...</p>
        ) : (dayData?.sessions?.length || 0) === 0 && makeups.length === 0 ? (
          <Empty
            title="No sessions scheduled this day"
            subtitle="No student has an individual session falling on this date. Try the prev/next day arrows or pick another date."
          />
        ) : (
          <>
            {makeups.length > 0 && (
              <div className="border-b border-brand-purple/10 bg-brand-purpleLight/30 p-4 sm:p-5" data-testid="ia-makeup-section">
                <p className="text-[11px] uppercase tracking-wider text-brand-purple font-heading mb-2.5 flex items-center gap-1.5">
                  <CalendarPlus className="w-3.5 h-3.5" /> Make-up Sessions Today
                </p>
                <ul className="divide-y divide-brand-purple/10 rounded-2xl border border-brand-purple/15 bg-white overflow-hidden" data-testid="ia-makeup-list">
                  {makeups.map((m) => (
                    <li key={m.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`ia-makeup-${m.id}`}>
                      <div className="min-w-0">
                        <p className="font-heading text-sm text-foreground">
                          {m.student_full_name || m.student_id}
                          {(m.time_start || m.time_end) ? <span className="text-brand-inkMuted ml-2"><Clock className="w-3 h-3 inline -mt-0.5 mr-1" />{m.time_start}{m.time_end ? ` – ${m.time_end}` : ""}</span> : null}
                        </p>
                        {m.note ? <p className="text-xs text-brand-inkMuted mt-0.5">{m.note}</p> : null}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => deleteMakeUp(m.id)}
                        className="h-8 w-8 p-0 rounded-lg text-brand-inkMuted hover:text-[#C62828] hover:bg-[#FDECEA]"
                        title="Delete make-up"
                        data-testid={`ia-makeup-delete-${m.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(dayData?.sessions?.length || 0) === 0 ? null : (
          <ul className="divide-y divide-brand-purple/10" data-testid="ia-sessions">
            {dayData.sessions.map((s) => {
              const k = sessionKey(s);
              const cur = drafts[k] || "";
              const isRecurring = s.kind === "day";
              const bal = sessionBalances[s.student_id] || { paid_remaining: 0, unpaid_attended: 0 };
              return (
                <li key={k} className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5" data-testid={`ia-session-${k}`}>
                  {/* Left: student + time */}
                  <div className="flex items-center gap-3 min-w-[300px]">
                    <div className="w-12 h-12 rounded-2xl bg-brand-purpleLight/60 text-brand-purple flex items-center justify-center shrink-0 font-heading text-sm font-bold uppercase">
                      {(s.student_full_name || "?").split(" ").slice(0, 2).map((p) => p[0]).join("").slice(0, 2)}
                    </div>
                    <div className="min-w-0">
                      <p className="font-heading font-semibold text-foreground uppercase truncate" data-testid={`ia-student-${s.student_id}`}>{s.student_full_name}</p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5">
                        {(s.time_start || s.time_end) && (
                          <span className="inline-flex items-center gap-1 text-xs text-brand-inkMuted"><Clock className="w-3.5 h-3.5" />{s.time_start}{s.time_end ? ` – ${s.time_end}` : ""}</span>
                        )}
                        <span
                          className={`inline-flex items-center gap-1 text-[10px] font-heading font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                            isRecurring
                              ? "bg-[#EEF2FF] text-[#4338CA]"
                              : "bg-[#FFF7E6] text-[#B45309]"
                          }`}
                          title={isRecurring ? "Recurring weekly slot" : "Specific date slot"}
                        >
                          {isRecurring ? <Repeat className="w-3 h-3" /> : <CalendarRange className="w-3 h-3" />}
                          {isRecurring ? "Recurring" : "Date"}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {s.individual_class_name && (
                          <span className="text-[10.5px] text-brand-inkMuted font-heading">{s.individual_class_name}</span>
                        )}
                        {s.teacher_name && (
                          <span className="text-[10.5px] text-brand-inkMuted inline-flex items-center gap-1"><UserIcon className="w-3 h-3" />{s.teacher_name}</span>
                        )}
                      </div>
                      {/* Compact balance chips so the user can see the student's
                          financial standing without opening a separate panel. */}
                      <div className="flex gap-1.5 mt-1.5">
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#E3F2FD] text-[#0D47A1] font-heading font-bold">🔵 {bal.paid_remaining} paid</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-heading font-bold ${bal.unpaid_attended > 0 ? "bg-[#FDECEA] text-[#C62828]" : "bg-slate-100 text-slate-500"}`}>🔴 {bal.unpaid_attended} unpaid</span>
                      </div>
                    </div>
                  </div>

                  {/* Middle: P/A/E buttons */}
                  <div className="flex-1 flex justify-start sm:justify-center">
                    <div className="inline-flex gap-2" role="group">
                      {STATUS.map((st) => {
                        const active = cur === st.id;
                        return (
                          <button
                            key={st.id}
                            type="button"
                            onClick={() => toggleMark(s, st.id)}
                            title={st.title}
                            data-testid={`ia-mark-${k}-${st.id}`}
                            className={`w-10 h-10 rounded-xl text-sm font-heading font-bold border transition ${active ? st.active : st.cls}`}
                          >
                            {st.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Right: action buttons */}
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setMakeUpFor({ student_id: s.student_id, full_name: s.student_full_name, individual_class_id: s.individual_class_id || "" })}
                      className="rounded-2xl border-brand-purple/20 text-brand-purple hover:bg-brand-purpleLight/60 h-10 px-3"
                      title="Log a make-up for this student"
                      data-testid={`ia-makeup-${s.student_id}`}
                    >
                      <CalendarPlus className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => saveOne(s)}
                      disabled={!!savingKey}
                      className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white h-10 px-4"
                      data-testid={`ia-save-${k}`}
                    >
                      <Save className="w-4 h-4 mr-1.5" /> {savingKey === k ? "Saving..." : "Save"}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
            )}
          </>
        )}
      </section>

      {/* Save day (bottom, big & on its own row) */}
      {dayData?.sessions?.length > 0 && (
        <div className="mt-6 flex justify-end">
          <Button
            onClick={saveAll}
            disabled={!!savingKey}
            className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white h-12 px-8 text-base"
            data-testid="ia-save-all"
          >
            <Save className="w-5 h-5 mr-2" /> {savingKey === "__all" ? "Saving..." : "Save day"}
          </Button>
        </div>
      )}

      {makeUpFor && (
        <IndividualMakeUpDialog
          branchId={branchId}
          studentId={makeUpFor.student_id}
          studentLabel={makeUpFor.full_name}
          individualClassId={makeUpFor.individual_class_id}
          defaultDate={selectedDate}
          onClose={() => setMakeUpFor(null)}
          onSaved={() => { setMakeUpFor(null); loadDay(); }}
        />
      )}

      {retentionPrompt && (
        <RetentionDeductionDialog
          branchId={branchId}
          prompt={retentionPrompt}
          onClose={() => setRetentionPrompt(null)}
        />
      )}

      <style>{`
        .ia-select {
          width: 100%; height: 2.75rem; border-radius: 1rem;
          border: 1px solid rgba(37, 99, 235, 0.18); background: #fff;
          padding: 0 0.75rem; font-size: 0.9rem; color: hsl(var(--foreground));
        }
        .ia-select:focus-visible { outline: none; box-shadow: 0 0 0 2px hsl(var(--ring)); }
      `}</style>
    </>
  );
}


function Field({ label, children }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-brand-inkMuted font-heading">{label}</p>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function ReadInput({ value }) {
  return <div className="ia-select flex items-center cursor-not-allowed text-foreground bg-white">{value}</div>;
}

function Stat({ label, value, tone }) {
  const tones = {
    default: "bg-white border-brand-purple/10 text-foreground",
    green: "bg-[#E8F8EE] border-[#118D57]/25 text-[#118D57]",
    red: "bg-[#FDECEA] border-[#C62828]/25 text-[#C62828]",
    orange: "bg-[#FFF3E0] border-[#E65100]/25 text-[#E65100]",
  };
  return (
    <div className={`rounded-2xl border px-4 py-3 ${tones[tone] || tones.default}`} data-testid={`ia-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="text-[11px] uppercase tracking-wider font-heading opacity-80">{label}</p>
      <p className="font-heading text-2xl font-bold mt-1 tabular-nums">{value}</p>
    </div>
  );
}


/* ------- Session balance chip -------
 * Phase-2 visual cue next to the student selector. Shows at a glance how
 * many paid sessions are still unattended (positive credit) and how many
 * sessions the student attended without paying (negative balance / debt).
 * Renders even when both counts are 0 so the user knows the chip exists.
 */
function SessionBalanceChip({ balance }) {
  const paid = balance?.paid_remaining ?? 0;
  const owed = balance?.unpaid_attended ?? 0;
  return (
    <div
      data-testid="ia-session-balance"
      className="inline-flex items-center gap-2 rounded-full bg-white border border-brand-purple/15 px-3 h-9 shadow-sm"
    >
      <span className="text-[11px] font-heading font-semibold text-brand-inkMuted uppercase tracking-wider">Session balance</span>
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E3F2FD] text-[#0D47A1] text-xs font-heading font-bold">
        🔵 {paid} paid
      </span>
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-heading font-bold ${owed > 0 ? "bg-[#FDECEA] text-[#C62828]" : "bg-slate-100 text-slate-500"}`}>
        🔴 {owed} unpaid
      </span>
    </div>
  );
}

function Empty({ title, subtitle }) {
  return (
    <div className="p-12 text-center" data-testid="ia-empty">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-brand-purpleLight flex items-center justify-center mb-3">
        <ClipboardCheck className="w-7 h-7 text-brand-purple" />
      </div>
      <p className="font-heading text-lg font-semibold text-foreground">{title}</p>
      {subtitle && <p className="text-brand-inkMuted mt-1 text-sm max-w-md mx-auto">{subtitle}</p>}
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/* Individual Make-up Class Dialog                                            */
/* -------------------------------------------------------------------------- */
function IndividualMakeUpDialog({ branchId, studentId, studentLabel, individualClassId, defaultDate, onClose, onSaved }) {
  const [date, setDate] = useState(defaultDate || fmtISO(new Date()));
  const [timeStart, setTimeStart] = useState("");
  const [timeEnd, setTimeEnd] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!date) { toast.error("Date is required"); return; }
    setSaving(true);
    try {
      await api.post(`/individual-attendance/make-up?branch_id=${branchId}`, {
        student_id: studentId,
        individual_class_id: individualClassId || "",
        date,
        time_start: timeStart,
        time_end: timeEnd,
        note,
      });
      toast.success("Make-up class logged");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md rounded-3xl" data-testid="ia-makeup-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Make-up Individual Session</DialogTitle>
          <DialogDescription className="text-brand-inkMuted uppercase">{studentLabel || "—"}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="font-heading">Date <span className="text-[#C62828]">*</span></Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-2xl h-11 mt-1.5" data-testid="ia-makeup-date" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-heading">Time Start</Label>
              <Input type="time" value={timeStart} onChange={(e) => setTimeStart(e.target.value)} className="rounded-2xl h-11 mt-1.5" data-testid="ia-makeup-start" />
            </div>
            <div>
              <Label className="font-heading">Time End</Label>
              <Input type="time" value={timeEnd} onChange={(e) => setTimeEnd(e.target.value)} className="rounded-2xl h-11 mt-1.5" data-testid="ia-makeup-end" />
            </div>
          </div>
          <div>
            <Label className="font-heading">Note</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Make-up for May 18 absence" className="rounded-2xl mt-1.5 min-h-[80px]" data-testid="ia-makeup-note" />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-2xl h-11">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white h-11 px-6" data-testid="ia-makeup-submit">{saving ? "Saving..." : "Log Make-up"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


/* ---------------- Retention-Fee deduction popup ---------------------------
 * Shown right after a session is marked ABSENT. Lets the operator decide
 * whether the absence should trigger a Retention Fee deduction, and if so,
 * how much. Skipping just closes the dialog with no effect.
 */
function RetentionDeductionDialog({ branchId, prompt, onClose }) {
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const parsed = Number(amount);
  const valid = Number.isFinite(parsed) && parsed > 0;

  async function submit() {
    if (!valid) { toast.error("Enter a positive amount to deduct"); return; }
    setSaving(true);
    try {
      await api.post("/student-accounts", {
        branch_id: branchId,
        student_id: prompt.student_id,
        category: "retention_fee",
        date: prompt.date,
        description: `Absence deduction — ${prompt.date}`,
        amount: -parsed,
        paid_amount: -parsed,
        notes: notes.trim() || `Deduction for absence on ${prompt.date}`,
        source: "outside",
        payment_method: "other",
        reference_no: "",
        payment_date: prompt.date,
      });
      toast.success(`Deducted ₱${parsed.toFixed(2)} from retention fee`);
      onClose && onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="rounded-3xl max-w-sm" data-testid="retention-deduction-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Wallet className="w-5 h-5 text-[#0F766E]" /> Retention Fee Deduction?
          </DialogTitle>
          <DialogDescription>
            <span className="font-semibold text-foreground">{prompt.student_name}</span> was marked absent on {prompt.date}. Would you like to deduct from their Retention Fee?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="font-heading">Amount to Deduct (₱)</Label>
            <Input
              type="number" min="0" step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="rounded-2xl h-11 mt-1.5 tabular-nums"
              autoFocus
              data-testid="retention-deduction-amount"
            />
          </div>
          <div>
            <Label className="font-heading">Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={400}
              placeholder="Optional reason / remarks"
              className="rounded-2xl mt-1.5"
              data-testid="retention-deduction-notes"
            />
          </div>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button type="button" variant="outline" onClick={onClose} className="rounded-2xl" data-testid="retention-deduction-skip">
            Skip (no deduction)
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={!valid || saving}
            className="rounded-2xl bg-[#0F766E] hover:bg-[#115E59] text-white"
            data-testid="retention-deduction-confirm"
          >
            {saving ? "Deducting..." : "Deduct"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
